<template>
  <div class="announcement-list">
    <div v-if="unreadForScope > 0" class="al-header">
      <button class="mark-all-btn" :disabled="markingAllRead" @click="markAllRead">
        <b-spinner v-if="markingAllRead" small class="mark-all-spinner" />
        {{ markingAllRead ? 'Marking…' : 'Mark all read' }}
      </button>
    </div>

    <div v-if="loading && !loadingMore" class="al-items">
      <div v-for="i in 5" :key="'skel-' + i" class="al-skeleton-card">
        <div class="skel-line" style="width: 74px; height: 16px;"></div>
        <div class="skel-line" style="width: 60%; height: 14px; margin-top: 12px;"></div>
        <div class="skel-line" style="width: 38%; height: 9px; margin-top: 8px;"></div>
        <div class="skel-line" style="width: 100%; height: 9px; margin-top: 8px;"></div>
        <div class="skel-line" style="width: 84%; height: 9px; margin-top: 6px;"></div>
      </div>
    </div>

    <div v-else-if="announcements.length" class="al-items">
      <AnnouncementCard v-for="announcement in announcements" :key="announcement.id" :announcement="announcement" @open="openAnnouncement" />
    </div>

    <div v-else class="al-state">
      <img src="@/assets/images/no-announcements.svg" class="al-state-icon" alt="" aria-hidden="true" />
      <p class="al-state-title">{{ searchQuery ? 'No matching announcements' : 'No announcements yet' }}</p>
      <p class="al-state-text">{{ searchQuery ? 'Try a different search.' : 'You’re all caught up — check back later.' }}</p>
    </div>

    <div v-if="nextPageToken" ref="loadMoreSentinel" class="al-loadmore">
      <button type="button" class="al-loadmore-btn" :disabled="loadingMore" @click="loadMore">
        {{ loadingMore ? 'Loading…' : 'Load more' }}
      </button>
    </div>

    <AnnouncementDetailModal :announcement="selectedAnnouncement" :visible="isDetailVisible" @close="closeDetail" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import AnnouncementCard from './AnnouncementCard.vue';
import AnnouncementDetailModal from './AnnouncementDetailModal.vue';

export default {
  name: 'AnnouncementList',
  components: { AnnouncementCard, AnnouncementDetailModal },
  props: {
    entityType: { type: String, default: null },
    entityId: { type: [String, Number], default: null },
    onlyUnread: { type: Boolean, default: false },
    onlyGlobal: { type: Boolean, default: false },
    searchQuery: { type: String, default: '' },
  },
  data() {
    return {
      announcements: [],
      nextPageToken: null,
      pageSize: 25,
      loading: false,
      loadingMore: false,
      loadSeq: 0,
      loadMoreObserver: null,
      selectedAnnouncement: null,
      isDetailVisible: false,
      markingAllRead: false,
    };
  },
  computed: {
    ...mapGetters('announcement', ['unreadCount']),

    scope() {
      return { entityType: this.entityType, entityId: this.entityId };
    },
    // The count/mark-all must match the visible list, so include the global + search filters too.
    countScope() {
      return { entityType: this.entityType, entityId: this.entityId, onlyGlobal: this.onlyGlobal, searchQuery: this.searchQuery || null };
    },
    unreadForScope() {
      return this.unreadCount(this.countScope);
    },
    scopeSignature() {
      return `${this.entityType}|${this.entityId}|${this.onlyUnread}|${this.onlyGlobal}|${this.searchQuery}`;
    },
  },
  watch: {
    scopeSignature() {
      this.loadAnnouncements();
    },
  },
  methods: {
    ...mapActions('announcement', ['fetchAnnouncements', 'fetchAnnouncementCount', 'logAnnouncementView', 'markAllAnnouncementsAsRead']),

    requestParams(extra = {}) {
      return { ...this.scope, searchQuery: this.searchQuery || null, onlyUnread: this.onlyUnread, onlyGlobal: this.onlyGlobal, pageSize: this.pageSize, ...extra };
    },

    async loadAnnouncements() {
      const seq = ++this.loadSeq;
      this.loading = true;
      this.loadingMore = false;
      this.nextPageToken = null;
      try {
        const result = await this.fetchAnnouncements(this.requestParams());
        if (seq !== this.loadSeq) return;
        this.announcements = result.payload || [];
        this.nextPageToken = result.nextPageToken || null;
        await this.fetchAnnouncementCount(this.countScope);
        this.refreshLoadMore();
      } catch (error) {
        if (seq === this.loadSeq) {
          this.announcements = [];
          this.nextPageToken = null;
        }
      } finally {
        if (seq === this.loadSeq) this.loading = false;
      }
    },
    async loadMore() {
      if (!this.nextPageToken || this.loadingMore) return;
      this.loadingMore = true;
      try {
        const result = await this.fetchAnnouncements(this.requestParams({ nextPageToken: this.nextPageToken }));
        this.announcements = this.announcements.concat(result.payload || []);
        this.nextPageToken = result.nextPageToken || null;
        this.refreshLoadMore();
      } finally {
        this.loadingMore = false;
      }
    },

    refreshLoadMore() {
      this.$nextTick(this.setupLoadMoreObserver);
    },
    setupLoadMoreObserver() {
      this.disconnectLoadMoreObserver();
      if (typeof IntersectionObserver === 'undefined') return;

      const sentinel = this.$refs.loadMoreSentinel;
      if (!sentinel) return;

      this.loadMoreObserver = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) this.loadMore();
      }, { rootMargin: '240px 0px' });
      this.loadMoreObserver.observe(sentinel);
    },
    disconnectLoadMoreObserver() {
      if (this.loadMoreObserver) {
        this.loadMoreObserver.disconnect();
        this.loadMoreObserver = null;
      }
    },

    async openAnnouncement(announcement) {
      this.selectedAnnouncement = announcement;
      this.isDetailVisible = true;
      if (announcement.isRead) return;

      await this.logAnnouncementView([announcement.id]);
      announcement.isRead = true;
      await this.fetchAnnouncementCount(this.countScope);
      if (this.entityType || this.entityId || this.onlyGlobal || this.searchQuery) await this.fetchAnnouncementCount();
    },
    closeDetail() {
      this.isDetailVisible = false;
      this.selectedAnnouncement = null;
    },

    async markAllRead() {
      if (this.markingAllRead) return;
      this.markingAllRead = true;
      try {
        await this.markAllAnnouncementsAsRead(this.countScope);
        this.announcements.forEach(announcement => { announcement.isRead = true; });
        if (this.entityType || this.entityId || this.onlyGlobal || this.searchQuery) await this.fetchAnnouncementCount();
      } finally {
        this.markingAllRead = false;
      }
    },
  },
  created() {
    this.loadAnnouncements();
  },
  beforeDestroy() {
    this.disconnectLoadMoreObserver();
  },
};
</script>

<style scoped>
.announcement-list {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.al-header {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0 2px;
}

.mark-all-btn {
  background: none;
  border: none;
  color: var(--primary-color);
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
}

.mark-all-btn:disabled {
  opacity: 0.6;
  cursor: default;
}

.mark-all-spinner {
  width: 12px;
  height: 12px;
  border-width: 0.12em;
  margin-right: 4px;
  vertical-align: middle;
}

.al-items {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.al-items >>> .announcement-card {
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 12px;
}

.al-state {
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 12px;
  padding: 48px 20px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.al-state-icon {
  width: 40px;
  height: 40px;
  color: #cbd5e1;
  margin-bottom: 8px;
}

.al-state-title {
  font-size: 1rem;
  font-weight: 600;
  color: #374151;
  margin: 0;
}

.al-state-text {
  font-size: 0.85rem;
  color: #6b7280;
  margin: 0;
}

.al-loadmore {
  display: flex;
  justify-content: center;
  padding: 6px 0 4px;
}

.al-loadmore-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 1.1rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: #fff;
  font-size: 0.82rem;
  font-weight: 500;
  color: #111827;
  cursor: pointer;
}

.al-loadmore-btn:hover {
  background: #f9fafb;
}

.al-loadmore-btn:disabled {
  opacity: 0.6;
  cursor: default;
}

.al-skeleton-card {
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 12px;
  padding: 14px 16px;
}

.skel-line {
  position: relative;
  display: block;
  background: #e2e2e2;
  border-radius: 4px;
  overflow: hidden;
}

.skel-line::after {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.65) 50%, rgba(255, 255, 255, 0) 100%);
  transform: translateX(-100%);
  animation: al-shimmer 1.6s infinite linear;
}

@keyframes al-shimmer {
  100% {
    transform: translateX(100%);
  }
}

@media (prefers-reduced-motion: reduce) {
  .skel-line::after {
    animation: none;
    opacity: 0;
  }
}
</style>
